import React from 'react';
import { Button, Popover, Select, Tag } from 'antd';
import { CloudServerOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { FunctionLanguage } from '../../../../../../api/opmApi';

export type ExecutionLogicMode = 'USER_DEFINED_FUNCTION' | 'LLM_MODEL';

export const EXECUTION_LOGIC_MODE_OPTIONS = [
  { label: '用户定义函数', value: 'USER_DEFINED_FUNCTION' as ExecutionLogicMode },
  { label: 'LLM大模型', value: 'LLM_MODEL' as ExecutionLogicMode },
];

export const LANGUAGE_OPTIONS = [
  { label: 'TypeScript', value: 'TYPESCRIPT' as FunctionLanguage },
  { label: 'Python', value: 'PYTHON' as FunctionLanguage },
];

export const MONACO_LANG_MAP: Record<FunctionLanguage, string> = {
  TYPESCRIPT: 'typescript',
  PYTHON: 'python',
};

const RUNTIME_ENVS = [
  {
    id: 'default',
    label: '默认环境',
    runtimes: [
      {
        name: 'TypeScript',
        version: '5.0.3',
        icon: '🟦',
        packages: [
          {
            name: 'intellix-sdk',
            version: 'latest',
            description: 'Intellix 平台 SDK',
          },
        ],
      },
      {
        name: 'Python',
        version: '3.12',
        icon: '🐍',
        packages: [
          {
            name: 'intellix-sdk',
            version: 'latest',
            description: 'Intellix 平台 SDK',
          },
        ],
      },
    ],
  },
] as const;

interface RuntimeEnvPopoverProps {
  envId: string;
}

interface RuntimeEnvSelectorProps {
  envId: string;
  onChange: (envId: string) => void;
}

/**
 * 从旧代码中提取函数体，合并到最新的函数签名里，避免切换 schema 后覆盖用户实现。
 */
export function mergeBodyIntoSignature(
  newSignature: string,
  oldCode: string,
  language: FunctionLanguage
): string {
  if (language === 'PYTHON') {
    const oldLines = oldCode.split('\n');
    const defIndex = oldLines.findIndex(line => /^\s*(async\s+)?def\s+/.test(line));
    if (defIndex === -1) return newSignature;
    const oldBody = oldLines.slice(defIndex + 1).join('\n');

    const newLines = newSignature.split('\n');
    const newDefIndex = newLines.findIndex(line => /^\s*(async\s+)?def\s+/.test(line));
    if (newDefIndex === -1) return newSignature;

    return [...newLines.slice(0, newDefIndex + 1), oldBody].join('\n');
  }

  const functionMatch = oldCode.match(/(async\s+)?function\s+\w+[^{]*\{/);
  if (!functionMatch || functionMatch.index === undefined) return newSignature;

  const bodyStart = functionMatch.index + functionMatch[0].length;
  const lastBraceIndex = oldCode.lastIndexOf('}');
  if (lastBraceIndex <= bodyStart) return newSignature;

  const body = oldCode.slice(bodyStart, lastBraceIndex);
  const newFunctionIndex = newSignature.lastIndexOf('{');
  const newLastBraceIndex = newSignature.lastIndexOf('}');
  if (newFunctionIndex === -1 || newLastBraceIndex === -1) {
    return newSignature;
  }

  return (
    newSignature.slice(0, newFunctionIndex + 1) +
    body +
    newSignature.slice(newLastBraceIndex)
  );
}

export function formatJsonText(value: string): string {
  return JSON.stringify(JSON.parse(value), null, 2);
}

export function validateJsonText(value: string): {
  valid: boolean;
  parsedValue: Record<string, any> | null;
  message: string;
} {
  try {
    const parsedValue = JSON.parse(value);
    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return {
        valid: false,
        parsedValue: null,
        message: '请输入 JSON 对象格式的数据',
      };
    }

    return {
      valid: true,
      parsedValue,
      message: 'JSON 校验通过',
    };
  } catch (error: any) {
    return {
      valid: false,
      parsedValue: null,
      message: error?.message || 'JSON 格式不正确',
    };
  }
}

export function RuntimeEnvPopover({ envId }: RuntimeEnvPopoverProps) {
  const env = RUNTIME_ENVS.find(item => item.id === envId) ?? RUNTIME_ENVS[0];

  return (
    <div style={{ width: 300 }}>
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
        <CloudServerOutlined style={{ marginRight: 6 }} />
        {env.label}
      </div>
      {env.runtimes.map((runtime, index) => (
        <div key={runtime.name}>
          {index > 0 && <div className="my-2 border-t border-gray-100" />}
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[13px] font-medium text-gray-700">
              {runtime.icon} {runtime.name}
            </span>
            <Tag color="blue" className="!m-0 !text-[11px]">
              v{runtime.version}
            </Tag>
          </div>
          <div className="pl-1">
            <div className="mb-1 text-[11px] text-gray-400">已安装包</div>
            {runtime.packages.map(pkg => (
              <div
                key={pkg.name}
                className="mb-1 flex items-center justify-between gap-2"
              >
                <span className="font-mono text-xs text-gray-700">
                  {pkg.name}
                </span>
                <div className="flex items-center gap-1">
                  {pkg.description && (
                    <span className="text-[11px] text-gray-400">
                      {pkg.description}
                    </span>
                  )}
                  <Tag color="green" className="!m-0 !text-[11px]">
                    {pkg.version}
                  </Tag>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function RuntimeEnvSelector({
  envId,
  onChange,
}: RuntimeEnvSelectorProps) {
  return (
    <div className="flex items-center gap-2">
      <CloudServerOutlined className="text-[13px] text-gray-400" />
      <Select
        value={envId}
        onChange={onChange}
        size="small"
        className="!w-[120px]"
        options={RUNTIME_ENVS.map(item => ({
          label: item.label,
          value: item.id,
        }))}
      />
      <Popover
        content={<RuntimeEnvPopover envId={envId} />}
        title={null}
        trigger="click"
        placement="bottomRight"
      >
        <Button
          size="small"
          type="text"
          icon={<InfoCircleOutlined />}
          className="!text-gray-400"
        />
      </Popover>
    </div>
  );
}
